import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useLang } from '../i18n'
import { useErrors, dismissError } from '../lib/errors'

/**
 * Global error popup. Any reported error (API failure, uncaught exception, render crash) is
 * shown centered on screen so the user is always informed. Errors are shown one at a time;
 * dismissing reveals the next. Rendered once at the app root, above everything else.
 */
export function ErrorOverlay() {
  const { t } = useLang()
  const errors = useErrors()
  const err = errors[0]
  const okRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (err) okRef.current?.focus()
  }, [err])

  if (!err) return null

  return createPortal(
    <div className="modal-backdrop" onMouseDown={() => dismissError(err.id)}>
      <div
        className="modal err-modal"
        role="alertdialog"
        aria-modal="true"
        aria-label={t('err_title')}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') dismissError(err.id)
        }}
      >
        <div className="modal-head">
          <span className="err-ic" aria-hidden="true">⚠</span>
          <h2>{t('err_title')}</h2>
          {errors.length > 1 && <span className="err-more">+{errors.length - 1}</span>}
          <button className="modal-close" aria-label={t('close')} onClick={() => dismissError(err.id)}>✕</button>
        </div>
        <div className="modal-body err-body">
          <p className="err-msg">{err.message}</p>
          {err.detail && <p className="err-detail">{err.detail}</p>}
        </div>
        <div className="err-acts">
          <button ref={okRef} className="mini ok" onClick={() => dismissError(err.id)}>{t('err_dismiss')}</button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
